import { useEffect, useRef, useCallback, useMemo } from "react";
import { setLayout, useMaterialUIController } from "context";
import logoEgead from "assets/images/egead/logo_main.png";
import "./Complete.css";

const CONFETTI_COLORS = [
  "#ffd700",
  "#ff6b9d",
  "#00d4ff",
  "#7fff00",
  "#ff4500",
  "#da70d6",
  "#fff",
];

const FIREWORK_COLORS = [
  "#ffd700",
  "#ff6b9d",
  "#00d4ff",
  "#7fff00",
  "#ff4500",
  "#ffffff",
  "#da70d6",
];

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function createFireworkParticle(x, y, color) {
  const angle = randomBetween(0, Math.PI * 2);
  const speed = randomBetween(2, 9);
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    color,
    alpha: 1,
    decay: randomBetween(0.012, 0.028),
    size: randomBetween(2, 4.5),
    trail: [],
  };
}

function launchFirework(canvas, particles) {
  const w = canvas.width;
  const h = canvas.height;
  const x = randomBetween(w * 0.15, w * 0.85);
  const y = randomBetween(h * 0.2, h * 0.55);
  const color = FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];
  const count = Math.floor(randomBetween(40, 80));
  for (let i = 0; i < count; i += 1) {
    particles.push(createFireworkParticle(x, y, color));
  }
}

function Complete() {
  const [, dispatch] = useMaterialUIController();
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const frameRef = useRef(null);
  const launchTimerRef = useRef(null);
  const confettiRootRef = useRef(null);

  useEffect(() => {
    setLayout(dispatch, "page");
  }, [dispatch]);

  const spawnConfetti = useCallback(() => {
    const root = confettiRootRef.current;
    if (!root) return;

    const count = 12;
    for (let i = 0; i < count; i += 1) {
      const el = document.createElement("span");
      el.className = "complete-confetti";
      const size = randomBetween(6, 14);
      const left = randomBetween(0, 100);
      const duration = randomBetween(3, 7);
      const delay = randomBetween(0, 2);
      const color = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];

      el.style.cssText = `
        left: ${left}%;
        width: ${size}px;
        height: ${size * 0.6}px;
        background: ${color};
        border-radius: ${Math.random() > 0.5 ? "50%" : "2px"};
        animation-duration: ${duration}s;
        animation-delay: ${delay}s;
      `;
      root.appendChild(el);
      setTimeout(() => el.remove(), (duration + delay) * 1000 + 100);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    const particles = particlesRef.current;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = "lighter";

      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 6) p.trail.shift();

        p.vx *= 0.98;
        p.vy *= 0.98;
        p.vy += 0.08;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        for (let t = 0; t < p.trail.length; t += 1) {
          const pt = p.trail[t];
          const trailAlpha = (t / p.trail.length) * p.alpha * 0.4;
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, p.size * 0.5, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = trailAlpha;
          ctx.fill();
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);

    const scheduleFirework = () => {
      launchFirework(canvas, particles);
      if (Math.random() > 0.5) {
        setTimeout(() => launchFirework(canvas, particles), randomBetween(150, 400));
      }
      launchTimerRef.current = setTimeout(scheduleFirework, randomBetween(400, 1200));
    };
    scheduleFirework();

    const confettiInterval = setInterval(spawnConfetti, 400);
    spawnConfetti();

    return () => {
      window.removeEventListener("resize", resize);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (launchTimerRef.current) clearTimeout(launchTimerRef.current);
      clearInterval(confettiInterval);
      particles.length = 0;
    };
  }, [spawnConfetti]);

  const stars = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: `${randomBetween(0, 100)}%`,
        top: `${randomBetween(0, 100)}%`,
        delay: `${randomBetween(0, 2)}s`,
        size: `${randomBetween(2, 4)}px`,
      })),
    []
  );

  return (
    <div className="complete-page">
      <div className="complete-bg" aria-hidden="true" />
      <div className="complete-stars" aria-hidden="true">
        {stars.map((s) => (
          <span
            key={s.id}
            className="complete-star"
            style={{
              left: s.left,
              top: s.top,
              animationDelay: s.delay,
              width: s.size,
              height: s.size,
            }}
          />
        ))}
      </div>
      <canvas ref={canvasRef} className="complete-canvas" aria-hidden="true" />
      <div ref={confettiRootRef} className="complete-confetti-layer" aria-hidden="true" />

      <div className="complete-content">
        <div className="complete-sparkle-ring" aria-hidden="true" />
        <div className="complete-logo-wrap">
          <img
            src={logoEgead}
            alt="e-GEAD Company"
            className="complete-logo"
            width={140}
            height={106}
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <h1 className="complete-title">
          Chúc Mừng Các Bạn Đã Hoàn Thành Cuộc Đua Của Mình!
        </h1>
        <p className="complete-subtitle">
          Các bạn đã vượt qua mọi thử thách — thật xuất sắc! Hãy tận hưởng khoảnh khắc chiến thắng này.
        </p>
        <div className="complete-badge-row">
          <span className="complete-badge complete-badge--gold">Hoàn thành xuất sắc</span>
          <span className="complete-badge complete-badge--pink">Về đích</span>
          <span className="complete-badge complete-badge--cyan">Chiến thắng</span>
        </div>
      </div>
    </div>
  );
}

export default Complete;
